// Recover a building floor's ORIGINAL floor-plan coordinate frame from the
// pathfinder snapshot, so a plan can be drawn straight on a page.
//
// The problem: the snapshot's structure polygons are lat/lng only, and drawing
// those on a bare canvas puts the building on the page at whatever bearing it
// happens to sit at on campus — fine over satellite imagery, a rotated jumble on
// a printed sheet. UMapsTools avoids this by reading the `.umapsraw`, which
// keeps the geometry in the floor plan's own pixel space; the website has no
// umapsraw.
//
// The way through: nav nodes in `nav_<slug>.json` carry BOTH `x`/`y` (the plan's
// pixel space) and `lat`/`lng`. The mapping between them is the similarity
// transform the authoring tool applied — rotation, uniform scale, translation —
// so it can be recovered exactly by least squares from those pairs, and then
// applied to the polygons, which only have lat/lng. That is a fit to the real
// transform rather than a guess at the building's orientation, and the residual
// says how well it worked.
//
// Pure and dependency-free so it can be checked outside a bundler.

const R = 6378137;                       // metres per radian, WGS-84 equatorial
const rad = d => (d * Math.PI) / 180;

/**
 * lat/lng → local metres, east-north-flipped so +y runs SOUTH.
 *
 * The flip matters: canvas y grows downward while latitude grows northward, so
 * without it the plan frame differs from the pixel frame by a REFLECTION, and a
 * rotation-only fit cannot express that — it would settle on a best-fit rotation
 * with a large residual and produce a mirrored plan. With the flip the two
 * frames differ by rotation and scale alone, which is exactly what the fit below
 * solves for.
 */
function toLocal(lat, lng, lat0, lng0) {
  const k = Math.cos(rad(lat0)) || 1;
  return { X: rad(lng - lng0) * R * k, Y: -rad(lat - lat0) * R };
}

/**
 * Fit lat/lng → plan pixels from nav nodes that carry both.
 *
 * Solves `q = a·p + b` over the complex plane, where p is the node's local
 * metres and q its stored pixel position. A complex multiplier IS a rotation
 * plus a uniform scale, which is precisely the transform the authoring tool
 * applies — so this is one linear least-squares solve, not an optimiser.
 *
 * @param {Array<{x:number,y:number,lat:number,lng:number}>} nodes
 * @returns {null | {
 *   project: (p:{lat:number,lng:number}) => {x:number,y:number},
 *   rmse: number,      // mean residual, in plan pixels
 *   n: number,         // pairs used
 * }}
 */
export function fitPlanFrame(nodes) {
  const pts = (nodes || []).filter(n =>
    Number.isFinite(n?.x) && Number.isFinite(n?.y) &&
    Number.isFinite(n?.lat) && Number.isFinite(n?.lng));
  // Two points define a similarity, but two points also fit ANY two points
  // perfectly — the residual would be 0 and mean nothing. Ask for enough that
  // the residual is evidence.
  if (pts.length < 4) return null;

  const lat0 = pts.reduce((s, n) => s + n.lat, 0) / pts.length;
  const lng0 = pts.reduce((s, n) => s + n.lng, 0) / pts.length;
  const P = pts.map(n => toLocal(n.lat, n.lng, lat0, lng0));
  const Q = pts.map(n => ({ X: n.x, Y: n.y }));

  const mean = arr => ({
    X: arr.reduce((s, v) => s + v.X, 0) / arr.length,
    Y: arr.reduce((s, v) => s + v.Y, 0) / arr.length,
  });
  const pm = mean(P), qm = mean(Q);

  // a = Σ (q-q̄)·conj(p-p̄) / Σ |p-p̄|²
  let numRe = 0, numIm = 0, den = 0;
  for (let i = 0; i < P.length; i++) {
    const px = P[i].X - pm.X, py = P[i].Y - pm.Y;
    const qx = Q[i].X - qm.X, qy = Q[i].Y - qm.Y;
    numRe += qx * px + qy * py;      // Re[(qx+i·qy)(px−i·py)]
    numIm += qy * px - qx * py;      // Im[…]
    den += px * px + py * py;
  }
  if (den < 1e-9) return null;       // every node stacked on one spot
  const aRe = numRe / den, aIm = numIm / den;
  if (!Number.isFinite(aRe) || !Number.isFinite(aIm) || (aRe === 0 && aIm === 0)) return null;

  const project = ({ lat, lng }) => {
    const { X, Y } = toLocal(lat, lng, lat0, lng0);
    const dx = X - pm.X, dy = Y - pm.Y;
    return { x: qm.X + aRe * dx - aIm * dy, y: qm.Y + aIm * dx + aRe * dy };
  };

  let sq = 0;
  for (const n of pts) {
    const f = project(n);
    sq += (f.x - n.x) ** 2 + (f.y - n.y) ** 2;
  }
  return { project, rmse: Math.sqrt(sq / pts.length), n: pts.length };
}

/**
 * The fraction of a ring set's wall length that runs within `tol` degrees of an
 * axis. The number UMapsTools quotes for choosing plan space over lat/lng
 * (93.3% vs 0.2%), so a frame can be checked with the same measure rather than
 * by eye.
 *
 * @param {Array<Array<{x:number,y:number}>>} rings
 */
export function axisAlignedFraction(rings, tol = 5) {
  let aligned = 0, total = 0;
  for (const ring of rings || []) {
    for (let i = 0; i + 1 < ring.length; i++) {
      const dx = ring[i + 1].x - ring[i].x, dy = ring[i + 1].y - ring[i].y;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;
      // Fold to [0, 45]: distance to the nearer of the two axes.
      let deg = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI) % 90;
      if (deg > 45) deg = 90 - deg;
      total += len;
      if (deg <= tol) aligned += len;
    }
  }
  return total > 0 ? aligned / total : 0;
}
